import React from "react";

/**
 * Roll Fast TMS — LoadingSkeleton primitive.
 *
 * Consistent shimmer skeleton used everywhere loading states are needed.
 *
 * Variants:
 *  - "card"  — a stack of grey placeholder bars.
 *  - "kpi"   — a row of KPI tiles.
 *  - "table" — header + N rows of shimmering cells.
 *  - "text"  — a few paragraph lines.
 *  - "page"  — hero + kpis + table combo (uses Card/Kpi/Table).
 *
 * Props: rows, cols, tiles, lines, label, testid, dark, className.
 */
const shimmer =
  "relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.6s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/5 before:to-transparent";

function bar(dark) {
  return dark ? "bg-neutral-800/70" : "bg-neutral-200";
}

function Kpi({ tiles, dark }) {
  const cols = { 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-2 md:grid-cols-4", 5: "grid-cols-2 md:grid-cols-5", 6: "grid-cols-2 md:grid-cols-6" };
  const gridCls = cols[tiles] || cols[4];
  const b = bar(dark);
  const items = [];
  for (let i = 0; i < tiles; i++) items.push(i);
  return (
    <div className={"grid gap-3 " + gridCls}>
      {items.map((i) => (
        <div key={i} className={b + " " + shimmer + " h-[86px] rounded-md"} />
      ))}
    </div>
  );
}

function Table({ rows, cols, dark }) {
  const b = bar(dark);
  const rowItems = [];
  for (let i = 0; i < rows; i++) rowItems.push(i);
  const colItems = [];
  for (let i = 0; i < cols; i++) colItems.push(i);
  return (
    <div className="w-full space-y-2">
      <div className={b + " " + shimmer + " h-9 rounded-md"} />
      {rowItems.map((r) => (
        <div
          key={r}
          className="grid gap-2"
          style={{ gridTemplateColumns: "repeat(" + cols + ", minmax(0, 1fr))" }}
        >
          {colItems.map((c) => (
            <div key={c} className={b + " " + shimmer + " h-8 rounded-md"} />
          ))}
        </div>
      ))}
    </div>
  );
}

function Text({ lines, dark }) {
  const b = bar(dark);
  const items = [];
  for (let i = 0; i < lines; i++) items.push(i);
  return (
    <div className="space-y-2">
      {items.map((i) => (
        <div
          key={i}
          className={b + " " + shimmer + " h-3 rounded"}
          style={{ width: 90 - i * 12 + "%" }}
        />
      ))}
    </div>
  );
}

function Card({ lines, dark }) {
  const b = bar(dark);
  const items = [];
  for (let i = 0; i < lines; i++) items.push(i);
  return (
    <div className={(dark ? "rf-card-dark" : "rf-card") + " p-4 space-y-3"}>
      {items.map((i) => (
        <div
          key={i}
          className={b + " " + shimmer + " h-4 rounded"}
          style={{ width: 100 - i * 15 + "%" }}
        />
      ))}
    </div>
  );
}

export function LoadingSkeleton(props) {
  const {
    variant = "card",
    rows = 4,
    cols = 5,
    lines = 3,
    tiles = 4,
    dark = true,
    label = "Loading",
    testid = "loading-skeleton",
    className = "",
  } = props;

  let content = null;
  if (variant === "kpi") content = <Kpi tiles={tiles} dark={dark} />;
  else if (variant === "table") content = <Table rows={rows} cols={cols} dark={dark} />;
  else if (variant === "text") content = <Text lines={lines} dark={dark} />;
  else if (variant === "page")
    content = (
      <div className="space-y-6">
        <div className={bar(dark) + " " + shimmer + " h-16 rounded-md w-2/3"} />
        <Kpi tiles={tiles} dark={dark} />
        <Table rows={rows} cols={cols} dark={dark} />
      </div>
    );
  else content = <Card lines={lines} dark={dark} />;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label}
      data-testid={testid}
      className={className}
    >
      {content}
      <span className="sr-only">{label}</span>
    </div>
  );
}

export default LoadingSkeleton;
